import { AlertCircle } from 'lucide-react'
import { InfoTooltip } from '@/components/InfoTooltip'

export interface ResolvedPromptPreviewProps {
  extractorDefault: { content?: string; isLoading: boolean; isError: boolean }
  baseline: { content?: string; isLoading: boolean; isError: boolean }
  documentPrompt: string
  mode: 'supplement' | 'override'
}

// Placed at the bottom of the Prompt tab, per Design PRD 10 - same
// elevated/surface-raised treatment as TestPanel.tsx, reused verbatim so the
// two "this is interactive, not just another form section" cards read as one
// visual language. Pure function of its props: resolution is local text
// concatenation/substitution, no network call of its own (unlike TestPanel,
// which calls the real identification engine).
export function ResolvedPromptPreview({ extractorDefault, baseline, documentPrompt, mode }: ResolvedPromptPreviewProps) {
  const isLoading = extractorDefault.isLoading || baseline.isLoading
  const isError = extractorDefault.isError || baseline.isError
  // Product baseline overrides the extractor default when set - same
  // resolution order as ExtractionSection's own "Extractor default" ->
  // "Baseline Prompt" fields.
  const resolvedBaseline = baseline.content && baseline.content.length > 0 ? baseline.content : (extractorDefault.content ?? '')
  const trimmedDocumentPrompt = documentPrompt.trim()

  return (
    <section className="flex flex-col gap-4 rounded-control border border-border bg-surface-raised p-4 shadow-[0_0_0_1px_rgba(76,141,255,0.15)]">
      <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
        Resolved Prompt Preview
        <InfoTooltip text="The actual prompt text this document type resolves to, combining the extractor default, the product's baseline prompt, and this document's own prompt - against your current unsaved edits." />
      </h2>

      {isLoading && <p className="text-meta text-text-secondary">Loading…</p>}

      {!isLoading && isError && (
        <div className="flex items-center gap-2 rounded-control border border-border bg-surface px-3 py-2 text-body text-text-secondary">
          <AlertCircle size={16} strokeWidth={1.5} className="shrink-0 text-text-secondary" />
          Failed to load one of the referenced prompt files.
        </div>
      )}

      {!isLoading && !isError && trimmedDocumentPrompt.length === 0 && (
        <pre className="whitespace-pre-wrap break-words rounded-control border border-border bg-surface px-3 py-2 font-mono text-meta text-text-primary">
          {resolvedBaseline.length > 0 ? (
            resolvedBaseline
          ) : (
            <span className="text-text-disabled">Nothing resolved yet - select an extractor and/or set a baseline prompt.</span>
          )}
        </pre>
      )}

      {!isLoading && !isError && trimmedDocumentPrompt.length > 0 && mode === 'override' && (
        <>
          {resolvedBaseline.length > 0 && (
            <pre className="whitespace-pre-wrap break-words rounded-control border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-meta text-text-secondary line-through">
              {resolvedBaseline}
            </pre>
          )}
          <pre className="whitespace-pre-wrap break-words rounded-control border border-success/40 bg-success/10 px-3 py-2 font-mono text-meta text-text-primary">
            {trimmedDocumentPrompt}
          </pre>
        </>
      )}

      {!isLoading && !isError && trimmedDocumentPrompt.length > 0 && mode === 'supplement' && (
        <pre className="whitespace-pre-wrap break-words rounded-control border border-success/40 bg-success/10 px-3 py-2 font-mono text-meta text-text-primary">
          {resolvedBaseline}
          {resolvedBaseline.length > 0 && '\n\n'}
          <span className="text-text-secondary">— Document Type Addition —</span>
          {'\n\n'}
          {trimmedDocumentPrompt}
        </pre>
      )}
    </section>
  )
}
